import type { MediaFile } from '@/lib/services/media/media.types'
import { cn } from '@/lib/utils'

type ImageTileMedia = Pick<MediaFile, 'width' | 'height'>

/** `hover` shows the details while the tile is hovered or focused. */
export type ImageTileMetadataVisibility = 'hover' | 'always'

interface ImageTileMetadataProps {
  media: ImageTileMedia
  title?: string
  /** Defaults to the image's dimensions. */
  description?: string
  visibility?: ImageTileMetadataVisibility
  className?: string
}

function formatImageDimensions(media: ImageTileMedia) {
  if (!media?.width || !media?.height) {
    return null
  }

  return `${media.width} x ${media.height}`
}

export default function ImageTileMetadata({
  media,
  title,
  description = undefined,
  visibility = 'hover',
  className,
}: ImageTileMetadataProps) {
  const tileDescription = description ?? formatImageDimensions(media)

  if (!title && !tileDescription) {
    return null
  }

  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-x-0 bottom-0 z-10 rounded-b-lg bg-linear-to-t from-black/78 via-black/50 to-transparent px-3 pb-3 pt-8 text-white transition duration-150',
        visibility === 'hover' &&
          'translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100',
        className
      )}
    >
      {title ? <div className="truncate text-sm font-semibold">{title}</div> : null}
      {tileDescription ? (
        <div className="truncate text-xs font-medium uppercase tracking-wide text-white/70">
          {tileDescription}
        </div>
      ) : null}
    </div>
  )
}
